import { ReadingTypes } from 'constants/readingTypes';

import type { ICriticalNoCallValues } from '../types';

const joinArray = (arr: string[]) => arr?.join(', ');

export const generateCriticalNoCallMessage = <T extends ICriticalNoCallValues>(
  data: T,
) => {
  const {
    readingTypeText,
    isCallIndicated,
    whyNotIndicated,
    whyNotIndicatedText,
    whoAttemptedCall,
    attemptOutcome,
    isReadingEscalated,
    escalatedVia,
    escalatedReasons,
    notEscalatedReasons,
    notEscalatedReasonsOther,
    escalatedViaOther,
  } = data;
  const escalatedViaWithoutOther = escalatedVia.filter(
    (item) => item !== 'Other',
  );
  if (escalatedViaOther) {
    escalatedViaWithoutOther.push(escalatedViaOther);
  }

  let message = '';

  message += `Nurse reviewed patient’s critical ${
    ReadingTypes[readingTypeText as keyof typeof ReadingTypes]
  } reading. `;

  if (isCallIndicated === 'true') {
    message += 'Call to patient indicated. ';
  }

  if (isCallIndicated === 'false' && whyNotIndicated.length > 0) {
    if (whyNotIndicated.includes('Other')) {
      const reordered = [...whyNotIndicated];
      const index = reordered.indexOf('Other');
      if (index !== -1) {
        reordered.splice(index, 1);
        reordered.push('Other');
      }
      message += `Call to patient not indicated due to: ${joinArray(
        reordered,
      )}: ${whyNotIndicatedText}. Nurse will continue to monitor for ongoing trends. `;
    }
    if (!whyNotIndicated.includes('Other')) {
      message += `Call to patient not indicated due to: ${joinArray(
        whyNotIndicated,
      )}. Nurse will continue to monitor for ongoing trends. `;
    }
  }

  if (whoAttemptedCall.length > 0) {
    message += `Nurse made two attempts to contact ${joinArray(
      whoAttemptedCall,
    )}. `;
  }

  if (attemptOutcome === 'Left voicemail') {
    message += `Nurse left voicemail. `;
  }
  if (attemptOutcome === 'Unable to leave voicemail') {
    message += `Unable to leave voicemail. `;
  }
  if (attemptOutcome === 'Unavailable') {
    if (whoAttemptedCall.length > 0) {
      message += `${joinArray(whoAttemptedCall)} unavailable. `;
    }
    if (whoAttemptedCall.length === 0) {
      message += `Patient unavailable. `;
    }
  }
  if (attemptOutcome === 'Requests callback') {
    if (whoAttemptedCall.length > 0) {
      message += `${joinArray(
        whoAttemptedCall,
      )} requests callback. Reading not cleared. `;
    }
    if (whoAttemptedCall.length === 0) {
      message += `Patient requests callback. Reading not cleared. `;
    }
  }
  if (attemptOutcome === 'Declined call') {
    if (whoAttemptedCall.length > 0) {
      message += `${joinArray(whoAttemptedCall)} declined call. `;
    }
    if (whoAttemptedCall.length === 0) {
      message += `Patient declined call. `;
    }
  }
  if (isReadingEscalated === 'true' && escalatedVia.length > 0) {
    message += `Nurse escalated reading to clinic via ${joinArray(
      escalatedViaWithoutOther,
    )}. `;
  }
  if (isReadingEscalated === 'true' && escalatedReasons.length > 0) {
    message += `Escalated reading due to: ${joinArray(escalatedReasons)}. `;
  }
  if (isReadingEscalated === 'false' && notEscalatedReasons.length > 0) {
    message += `Reading not escalated to clinic due ${joinArray(
      notEscalatedReasons,
    )}. `;
    if (notEscalatedReasons.includes('Other')) {
      message += `${notEscalatedReasonsOther} `;
    }
  }

  message += '\n';

  return message.trim();
};
